import FirebaseModel from './model';
import firebase from 'firebase/app';
import { firestore } from 'core/firebase';
import { EventEmitter } from 'events';
import FS from './events';

export const COLLECTION_REGISTER: FirebaseCollection = [];

type CollectionSettings = {
  path: String,
  type?: FirebaseModel,
  parent?: FirebaseModel,
  query?: (c: firebase.firestore.CollectionReference) => firebase.firestore.Query
};

export default class FirebaseCollection<T> extends EventEmitter {
  models: FirebaseModel[] = [];
  settings: CollectionSettings;
  loaded: Boolean;
  snapshot: firebase.firestore.QuerySnapshot;
  lastUpdate = 0;

  _queries: firebase.firestore.Query[] = [];
  _subscriptions: Function[] = [];
  _batch: firebase.firestore.WriteBatch;

  constructor(settings: CollectionSettings) {
    super();
    COLLECTION_REGISTER.push(this);
    if (!settings.path) throw Error('Missing path');
    this.settings = settings;
  }

  where(path: String, whereFilterOp: firebase.firestore.WhereFilterOp, value: any, or?: Boolean) {
    if (!this._queries.length || or) this._queries.push(this.ref());
    let i = this._queries.length - 1;
    this._queries[i] = this._queries[i].where(path, whereFilterOp, value);
    return this;
  }

  or(path: String, whereFilterOp: firebase.firestore.WhereFilterOp, value: any): this {
    this.where(path, whereFilterOp, value, true);
    return this;
  }

  path(): String {
    const { parent, path } = this.settings;
    if (parent) {
      return parent.path() + '/' + path;
    }
    return path;
  }

  getActiveKey(): String {
    return this.acitveKey;
  }

  active(key?: String): T {
    if (key) this.acitveKey = key;
    return this.get(this.acitveKey);
  }

  get(key: String): T {
    if (!key) return false;
    for (let model of this.models) {
      if (key === model.key) return model;
    }

    const model = this._new(key);
    this.models.push(model);
    return model;
  }

  list(): T[] {
    return this.models.filter(m => m.loaded);
  }

  listData() {
    return this.models.filter(m => m.loaded).map(m => m.data());
  }

  getType(): FirebaseModel {
    return this.settings.type || FirebaseModel;
  }

  getNewKey() {
    return this.getType().newKey();
  }

  add(model: Object, key?: String): T {
    if (!key) key = this.getNewKey();
    const m = this.get(key);
    m.set(model);
    return m;
  }

  addPromise(model: Object, key?: String): Promise<T> {
    if (!key) key = this.getType().newKey();
    const m = this.get(key);
    return m.set(model).then(() => m);
  }

  _new(key: String): FirebaseModel {
    const Type = this.getType();
    return new Type(key, this);
  }

  remove(key: String) {
    for (let i = 0; i < this.models.length; i++) {
      if (this.models[i].key === key) {
        return this.models.splice(i, 1);
      }
    }
  }

  ref(): firebase.firestore.CollectionReference {
    return firestore.collection(this.path());
  }

  query(): firebase.firestore.Query[] {
    return this._queries.length ? this._queries : [this.ref()];
  }

  isQueryEqual(query: firebase.firestore.Query[]) {
    if (!this._lastQuery || this._lastQuery.length !== query.lenght) return false;
    for (let i = 0; i < query.length; i++) {
      if (!query[i].isEqual(this._lastQuery[i])) return false;
    }

    return true;
  }

  watch(): this {
    const queries = this.query();
    if (!this.isQueryEqual(queries)) {
      this.unwatch();
      for (let m of this.models) m.unwatch();
      this.removeAllListeners();
      this._lastQuery = queries;
      this._subscriptions = queries.map(q => q.onSnapshot(this.handleUpdate));
      this._queries = [];
      this.models = [];
    }

    return this;
  }

  unwatch(): this {
    for (let func of this._subscriptions) func();
    this._subscriptions = [];
    this._lastQuery = false;
    return this;
  }

  fetch(): Promise<T[]> {
    const query = this.query();
    this._queries = [];

    return Promise.all(query.map(q => q.get())).then(res => {
      const promises = [];
      const models = {};
      for (let col of res) {
        for (let doc of col.docs) {
          if (!models[doc.id]) {
            models[doc.id] = this._new(doc.id);
            promises.push(models[doc.id].init(doc));
          }
        }
      }

      return Promise.all(promises).then(() => {
        return Object.values(models);
      });
    });
  }

  isBatchActive() {
    return !!this._batch;
  }

  batch(): this {
    if (!this._batch) this._batch = firestore.batch();
    return this;
  }

  commit(): Promise<void> {
    const batch = this._batch;
    delete this._batch;
    return batch.commit();
  }

  onChildUpdate(cb: (model: T) => void): this {
    this.addListener(FS.childUpdated, cb);
    for (let m of this.models) cb(m);
    return this;
  }

  offChildUpdate(cb: (model: T) => void): this {
    this.removeListener(FS.childUpdated, cb);
    return this;
  }

  onChildRemove(cb: (model: T) => void): this {
    this.addListener(FS.childRemoved, cb);
    return this;
  }

  offChildRemove(cb: (model: T) => void): this {
    this.removeListener(FS.childRemoved, cb);
    return this;
  }

  onUpdate(cb: (model: this) => void): this {
    this.addListener(FS.updated, cb);
    if (this.loaded) cb(this);
    return this;
  }

  offUpdate(cb: (model: FirebaseCollection) => void): this {
    this.removeListener(FS.updated, cb);
    return this;
  }

  handleUpdate = async(snap: firebase.firestore.QuerySnapshot) => {
    this.loaded = true;
    this.snapshot = snap;
    this.models = this.models.slice();
    await snap.docChanges().forEach(this.handleChangeDoc);
    clearTimeout(this.updateTimeout);
    this.updateTimeout = setTimeout(this.triggerUpdated, 20);
    return this.models;
  };

  triggerUpdated = () => {
    this.emit(FS.updated, this);
  };

  handleChangeDoc = async({ doc, type }: firebase.firestore.DocumentChange) => {
    if (this.lastKey === doc.id && Date.now() - this.lastUpdate < 10) return;
    if (type === 'removed') {
      this.remove(doc.id);
      this.emit(FS.childRemoved, doc.id);
    } else {
      const model = this.get(doc.id);
      await model.init(doc);
      this.emit(FS.childUpdated, model);
    }

    this.lastUpdate = Date.now();
    this.lastKey = doc.id;
  };
}
